import React, { useEffect, useState } from 'react';
import { Card, CardContent } from '../ui/card';
import { Database, RefreshCw, Image, QrCode } from 'lucide-react';
import { Button } from '../ui/button';
import { api } from '../../lib/store';

function fmtBytes(b) {
  if (!b && b !== 0) return '—';
  if (b < 1024) return `${b} B`;
  if (b < 1024 * 1024) return `${(b / 1024).toFixed(1)} KB`;
  if (b < 1024 * 1024 * 1024) return `${(b / (1024 * 1024)).toFixed(1)} MB`;
  return `${(b / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export default function StorageStatsCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const r = await api.getStorageStats();
      setData(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load storage stats');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <Card className="border-[#E2E6D9]" data-testid="storage-stats-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold text-[#1A1C16] flex items-center gap-2">
            <Database className="w-4 h-4 text-[#556B2F]" /> Storage (MongoDB)
          </h3>
          <Button
            variant="ghost" size="sm"
            onClick={load}
            disabled={loading}
            className="h-7 px-2 text-xs"
            data-testid="storage-refresh-btn"
          >
            <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>

        {error && <p className="text-xs text-red-600">{error}</p>}

        {data && (
          <>
            <div className="grid grid-cols-3 gap-3 mb-3">
              <div className="bg-[#F5F6F0] rounded p-2 text-center">
                <p className="text-[10px] text-[#8F9485] uppercase tracking-wide">Total</p>
                <p className="text-lg font-bold text-[#1A1C16] font-mono-data">
                  {fmtBytes(data.total.bytes)}
                </p>
                <p className="text-[10px] text-[#5C6150]">{data.total.count} file{data.total.count === 1 ? '' : 's'}</p>
              </div>
              <div className="bg-[#F5F6F0] rounded p-2 text-center">
                <p className="text-[10px] text-[#8F9485] uppercase tracking-wide flex items-center justify-center gap-1">
                  <Image className="w-3 h-3" /> Products
                </p>
                <p className="text-lg font-bold text-[#1A1C16] font-mono-data">
                  {fmtBytes(data.product_images.bytes)}
                </p>
                <p className="text-[10px] text-[#5C6150]">{data.product_images.count}</p>
              </div>
              <div className="bg-[#F5F6F0] rounded p-2 text-center">
                <p className="text-[10px] text-[#8F9485] uppercase tracking-wide flex items-center justify-center gap-1">
                  <QrCode className="w-3 h-3" /> QR Lists
                </p>
                <p className="text-lg font-bold text-[#1A1C16] font-mono-data">
                  {fmtBytes(data.qr_price_lists.bytes)}
                </p>
                <p className="text-[10px] text-[#5C6150]">{data.qr_price_lists.count}</p>
              </div>
            </div>

            {(data.largest_product_images.length > 0 || data.largest_qr_images.length > 0) && (
              <details className="text-xs text-[#5C6150]">
                <summary className="cursor-pointer hover:text-[#1A1C16] py-1">
                  Largest files (top 5 each)
                </summary>
                <div className="mt-2 space-y-1 pl-2">
                  {data.largest_product_images.slice(0, 5).map((f, i) => (
                    <div key={i} className="flex justify-between gap-2">
                      <span className="truncate flex-1" title={f.filename}>
                        <Image className="w-3 h-3 inline mr-1" />
                        {f.filename}
                      </span>
                      <span className="font-mono-data text-[#1A1C16] shrink-0">{fmtBytes(f.size_bytes)}</span>
                    </div>
                  ))}
                  {data.largest_qr_images.slice(0, 5).map((f, i) => (
                    <div key={`qr-${i}`} className="flex justify-between gap-2">
                      <span className="truncate flex-1" title={f.label}>
                        <QrCode className="w-3 h-3 inline mr-1" />
                        {f.label || f.id}
                      </span>
                      <span className="font-mono-data text-[#1A1C16] shrink-0">{fmtBytes(f.size_bytes)}</span>
                    </div>
                  ))}
                </div>
              </details>
            )}
          </>
        )}

        {loading && !data && <p className="text-xs text-[#8F9485]">Loading…</p>}
      </CardContent>
    </Card>
  );
}
